/**
 * A2A English Writing Service
 * 
 * Service wrapper for A2A protocol communication with the A2AEnglishAI backend.
 * Provides methods for writing tools features:
 * - Writing examples
 * - Sentence expansion (via DeepSeek AI)
 * - Composition CRUD operations
 */

import englishWritingClient from '../a2aClient';

class A2AEnglishWritingService {
  constructor() {
    this.client = englishWritingClient;
  }

  // ============================================================================
  // Writing Examples Methods
  // ============================================================================

  /**
   * Get writing examples
   * @param {Object} params - Query parameters
   * @param {string} params.category - Filter by category (optional)
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20)
   * @returns {Promise<Object>} List of writing examples
   */
  async getWritingExamples(params = {}) {
    const {
      category = null,
      page = 1,
      limit = 20
    } = params;

    // Always log (even in production) for debugging
    console.log('[A2A Service] getWritingExamples: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      agentId: this.client.agentId,
      params,
      hasClient: !!this.client,
      capabilitiesLoaded: this.client.getCapabilities()?.length || 0
    });

    try {
      // Ensure capabilities are initialized
      const capabilities = this.client.getCapabilities();
      if (!capabilities || capabilities.length === 0) {
        console.warn('[A2A Service] Capabilities not loaded yet, attempting to initialize...');
        await this.client.discoverCapabilities();
      }
      
      const result = await this.client.executeTask('get_writing_examples', {
        category,
        page,
        limit
      });
      
      // Always log (even in production) for debugging
      console.log('[A2A Service] getWritingExamples: Success', {
        hasResult: !!result,
        hasExamples: !!(result?.examples),
        examplesCount: result?.examples?.length || 0,
        success: result?.success
      });
      
      return result;
    } catch (error) {
      console.error('[A2A Service] getWritingExamples: Error', {
        message: error.message,
        name: error.name,
        code: error.code,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  // ============================================================================
  // Sentence Expansion Methods (via DeepSeek AI)
  // ============================================================================

  /**
   * Expand a sentence using DeepSeek AI
   * @param {string} keyword - Base keyword to expand
   * @param {Object} options - Expansion options
   * @param {string} options.context - Context for expansion (default: 'general')
   * @param {string} options.expansion_type - Type of expansion (default: 'descriptive')
   * @returns {Promise<Object>} Expanded sentences with steps
   */
  async expandSentence(keyword, options = {}) {
    const {
      context = 'general',
      expansion_type = 'descriptive'
    } = options;

    console.log('[A2A Service] expandSentence: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      agentId: this.client.agentId,
      keyword,
      options
    });

    try {
      const result = await this.client.executeTask('expand_english_sentence', {
        keyword,
        context,
        expansion_type
      }, {
        timeout: 60000  // 60 seconds for AI expansion
      });
      
      console.log('[A2A Service] expandSentence: Success', {
        hasResult: !!result,
        hasExpandedSentences: !!(result?.expandedSentences),
        stepsCount: result?.expandedSentences?.length || 0,
        success: result?.success
      });
      
      // If backend returns success: false, treat it as an error
      if (result && result.success === false) {
        const errorMsg = result.error || 'Backend returned success: false';
        console.warn('[A2A Service] expandSentence: Backend returned success: false', errorMsg);
        throw new Error(errorMsg);
      }
      
      return result;
    } catch (error) {
      console.error('[A2A Service] expandSentence: Error', {
        message: error.message,
        name: error.name,
        code: error.code,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  // ============================================================================
  // Composition Methods
  // ============================================================================

  /**
   * Save a composition
   * @param {Object} compositionData - Composition data
   * @param {string} compositionData.title - Composition title
   * @param {string} compositionData.content - Final composition text
   * @param {string} compositionData.style - Composition style (default: 'composition')
   * @param {number} compositionData.word_length - Word length (default: 0)
   * @param {Array} compositionData.nodes_data - Nodes data (JSONB)
   * @param {string} compositionData.category - Category (default: 'AIComposition')
   * @param {string} compositionData.user_id - User ID (optional)
   * @returns {Promise<Object>} Saved composition
   */
  async saveComposition(compositionData) {
    console.log('[A2A Service] saveComposition: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      agentId: this.client.agentId,
      title: compositionData.title
    });

    try {
      const result = await this.client.executeTask('save_writing_composition', {
        user_id: compositionData.user_id || null,
        title: compositionData.title,
        content: compositionData.content,
        style: compositionData.style || 'composition',
        word_length: compositionData.word_length || 0,
        nodes_data: compositionData.nodes_data || [],
        category: compositionData.category || 'AIComposition'
      });
      
      console.log('[A2A Service] saveComposition: Success', {
        hasResult: !!result,
        hasComposition: !!(result?.composition),
        success: result?.success
      });
      
      // If backend returns success: false, treat it as an error
      if (result && result.success === false) {
        const errorMsg = result.error || 'Backend returned success: false';
        console.warn('[A2A Service] saveComposition: Backend returned success: false', errorMsg);
        throw new Error(errorMsg);
      }
      
      return result;
    } catch (error) {
      console.error('[A2A Service] saveComposition: Error', {
        message: error.message,
        name: error.name,
        code: error.code,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  /**
   * Get compositions
   * @param {Object} params - Query parameters
   * @param {string} params.user_id - Filter by user ID (optional)
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20)
   * @returns {Promise<Object>} List of compositions
   */
  async getCompositions(params = {}) {
    const {
      user_id = null,
      page = 1,
      limit = 20
    } = params;

    console.log('[A2A Service] getCompositions: Calling A2A backend', {
      serverUrl: this.client.remoteUrl,
      agentId: this.client.agentId,
      params
    });

    try {
      const result = await this.client.executeTask('get_compositions', {
        user_id,
        page,
        limit
      });
      
      console.log('[A2A Service] getCompositions: Success', {
        hasResult: !!result,
        hasCompositions: !!(result?.compositions),
        compositionsCount: result?.compositions?.length || 0,
        success: result?.success
      });
      
      // If backend returns success: false, treat it as an error
      if (result && result.success === false) {
        const errorMsg = result.error || 'Backend returned success: false';
        console.warn('[A2A Service] getCompositions: Backend returned success: false', errorMsg);
        throw new Error(errorMsg);
      }
      
      return result;
    } catch (error) {
      console.error('[A2A Service] getCompositions: Error', {
        message: error.message,
        name: error.name,
        code: error.code,
        serverUrl: this.client.remoteUrl
      });
      throw error;
    }
  }

  // ============================================================================
  // Context Detection & UX Negotiation
  // ============================================================================

  /**
   * Detect user context (device, screen size, network, etc.)
   * @returns {Object} User context information
   */
  detectContext() {
    return this.client.detectContext();
  }

  /**
   * Negotiate UX format with backend
   * @param {Object} context - User context (optional, will detect if not provided)
   * @returns {Promise<Object>} Negotiated UX format
   */
  async negotiateUX(context = null) {
    const userContext = context || this.detectContext();
    return await this.client.negotiateUX(userContext);
  }
}

// Export singleton instance
const a2aEnglishWritingService = new A2AEnglishWritingService();
export default a2aEnglishWritingService;

